import React, { useContext, useEffect, useState } from 'react'
import { useParams } from '@fs/zion-router'
import axios, { AxiosError } from '@fs/zion-axios'
import { setEx } from '@fs/zion-experiments'
import readData from './s3data-reader'
import { AncestorData } from '../types/data-types'
import { TfSource } from '../types/tf-types'
import { CustomWindow } from '../types/window'

type Store = {
  data?: AncestorData
  error?: Error
  pid?: string
  loading: boolean
  notFound: boolean
  gone: boolean
  movedTo?: string
  CF?: boolean
}

type DataContext = {
  store: Store
  setStore?: React.Dispatch<React.SetStateAction<Store>>
}

const PrivateContext = React.createContext<DataContext>({ store: initStore() })

export default function useData(): AncestorData | undefined {
  const { store } = useContext(PrivateContext)

  if (!store) throw new Error('useData must be used within a DataContext')
  return store.data
}

export function useDataContext(): DataContext {
  return useContext(PrivateContext)
}

function initStore(pid?: string, data?: AncestorData, error?: AxiosError): Store {
  const loading = !data && !error
  const notFound = error?.response?.status === 404
  const gone = error?.response?.status === 410
  const movedTo = error?.response?.status === 301 ? error?.response?.headers.location : undefined
  const win = window as CustomWindow

  return {
    pid,
    data,
    error,
    loading,
    notFound,
    gone,
    movedTo,
    CF: win?.SERVER_DATA?.ANCESTORS?.CF || false,
  }
}

const genericAxios = axios.create()

function updatePublic(updatedSources: TfSource[], source: TfSource, publicProp: string, publicValue: boolean): void {
  let foundSource: TfSource | undefined = updatedSources.find((us) => {
    return us.sourceLink === source.sourceLink
  })
  if (!foundSource) {
    foundSource = { ...source }
    updatedSources.push(foundSource)
  }
  foundSource[publicProp] = publicValue
}

export async function updateSourcePublicImages(sources: TfSource[]): Promise<TfSource[]> {
  const updatedSources: TfSource[] = []
  const promises: Promise<boolean>[] = []
  sources.forEach((source) => {
    if (source.sourceImageLink) {
      promises.push(
        genericAxios
          .head(source.sourceImageLink)
          .then(() => {
            updatePublic(updatedSources, source, 'imagePublic', true)
          })
          .catch(() => {
            updatePublic(updatedSources, source, 'imagePublic', false)
          })
      )
    }
    if (source.sourceThumbnailLink) {
      promises.push(
        genericAxios
          .head(source.sourceThumbnailLink)
          .then(() => {
            updatePublic(updatedSources, source, 'thumbnailPublic', true)
          })
          .catch(() => {
            updatePublic(updatedSources, source, 'thumbnailPublic', false)
          })
      )
    }
  })
  return Promise.all(promises).then(() => {
    const newSources: TfSource[] = []
    sources.forEach((originalSource) => {
      const newSource: TfSource | undefined = updatedSources.find((source) => {
        return source.sourceLink === originalSource.sourceLink
      })
      if (newSource) {
        newSources.push(newSource)
      }
    })
    return newSources
  })
}

export type DataProps = {
  pid?: string
  data?: AncestorData
  error?: Error
  children?: React.ReactElement
}
export function DataContext({ pid, data, error, children }: DataProps): JSX.Element {
  const [store, setStore] = useState(initStore(pid, data, error))
  const params = useParams()
  if (!pid) pid = params.pid

  useEffect(() => {
    let cancelled = false
    async function read(id: string): Promise<void> {
      let d: AncestorData | undefined
      let e: AxiosError | undefined
      try {
        d = await readData(id)
      } catch (err) {
        e = err
      }
      try {
        if (d?.sources?.sources) {
          d.sources.sources = await updateSourcePublicImages(d.sources?.sources || [])
        }
      } catch (err) {
        e = err
      }
      if (!cancelled) {
        setStore(initStore(id, d, e))
      }
    }
    if (!cancelled) {
      if (pid && !store.data && !store.error) {
        read(pid)
      }
    }

    return () => {
      cancelled = true
    }
  }, [store, pid])
  const value: DataContext = { store, setStore }

  return <PrivateContext.Provider value={value}>{children}</PrivateContext.Provider>
}
